import React, { useState, useEffect } from 'react';
import { Check, Gift, Loader2 } from 'lucide-react';
import { type AppView } from '../src/routes/slugs';
import { trackEvent } from '../services/analytics';
import { api } from '../services/api';

interface Step {
  id: string;
  label: string;
  done: boolean;
}

interface ChecklistData {
  steps: Step[];
  completed: number;
  total: number;
  allDone: boolean;
  rewardClaimed: boolean;
}

const STEP_NAV: Record<string, AppView> = {
  upload_photo: 'properties',
  first_render: 'properties',
  profile_complete: 'profile',
  invite_teammate: 'team',
  profile: 'profile',
  first_property: 'properties',
  add_property: 'properties',
  complete_profile: 'profile',
  invite_team: 'team',
  subscription: 'subscription',
};

interface Props {
  onCreditsUpdate?: (credits: number) => void;
  onNavigate?: (view: AppView) => void;
  refreshTrigger?: number;
}

export const OnboardingChecklist: React.FC<Props> = ({ onCreditsUpdate, onNavigate, refreshTrigger = 0 }) => {
  const [data, setData] = useState<ChecklistData | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    const fetchChecklist = () => {
      api.get<ChecklistData>('/api/onboarding/checklist')
        .then(setData)
        .catch(() => {});
    };

    fetchChecklist();

    // `refreshTrigger` is never bumped by any current caller, so without this
    // the checklist only ever fetches once on mount and never reflects
    // actions (photo upload, first render, profile completion, invite) taken
    // later in the same long-lived session — existing users who don't get a
    // full remount never see steps flip to done. Re-fetch whenever the tab
    // regains focus/visibility, which reliably covers "did something in
    // another view/tab and came back" without requiring every call site to
    // wire up refreshTrigger.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchChecklist();
    };
    window.addEventListener('focus', fetchChecklist);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', fetchChecklist);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshTrigger]);

  const claimReward = async () => {
    setClaiming(true);
    try {
      const result = await api.post<{ ok?: boolean; credits?: number }>('/api/onboarding/claim-reward', {});
      if (result.ok) {
        setClaimed(true);
        onCreditsUpdate?.(result.credits ?? 0);
        trackEvent('onboarding_reward_claimed');
      }
    } finally {
      setClaiming(false);
    }
  };

  if (!data) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  if (data.rewardClaimed && !claimed) return null;

  if (claimed) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent/12 via-accent/6 to-white border border-accent/25 p-6 md:p-7 mb-4 text-center">
        <div className="w-12 h-12 rounded-xl bg-accent text-white flex items-center justify-center mx-auto mb-3 shadow-[0_6px_16px_-6px_rgba(241,110,0,0.5)]">
          <Gift size={22} />
        </div>
        <h3 className="font-sans text-lg md:text-xl font-bold tracking-[-0.015em] text-ink mb-1">+10 credits ontvangen!</h3>
        <p className="text-sm text-ink/60 mb-4 max-w-md mx-auto">
          Goed bezig. Je hebt alle stappen voltooid.
        </p>
        <button onClick={() => setClaimed(false)} className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink/45 hover:text-ink transition-colors">
          Sluiten
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-ink text-white p-5 md:p-7 mb-4 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.5)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 gap-4">
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-white/45 mb-1.5">Aan de slag</p>
          <h3 className="font-sans text-lg md:text-xl font-bold tracking-[-0.02em] text-white">
            {data.completed} van {data.total} stappen klaar
          </h3>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-white/60 shrink-0">
          <Gift size={14} className={data.allDone ? 'text-accent' : 'text-white/30'} />
          <span className={data.allDone ? 'text-accent' : ''}>+10 credits</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Step tiles - horizontaal naast elkaar */}
      <div
        className="grid gap-2 md:gap-2.5"
        style={{ gridTemplateColumns: `repeat(${data.steps.length}, minmax(0, 1fr))` }}
      >
        {data.steps.map((step, idx) => (
          <StepTile
            key={step.id}
            index={idx + 1}
            label={step.label}
            done={step.done}
            onClick={(() => { const v = STEP_NAV[step.id]; return !step.done && onNavigate && v ? () => onNavigate(v) : undefined; })()}
          />
        ))}
      </div>

      {/* Claim button */}
      {data.allDone && !data.rewardClaimed && (
        <button
          onClick={claimReward}
          disabled={claiming}
          className="w-full mt-5 bg-accent hover:bg-accent-hover text-white py-3 rounded-xl font-bold text-sm shadow-[0_8px_22px_-8px_rgba(241,110,0,0.55)] hover:shadow-[0_12px_30px_-10px_rgba(241,110,0,0.6)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {claiming ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
          Claim je 10 bonus credits
        </button>
      )}
    </div>
  );
};

const StepTile: React.FC<{ index: number; label: string; done: boolean; onClick?: () => void }> = ({ index, label, done, onClick }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    className={`group relative rounded-xl border p-3 flex flex-col items-start gap-2 min-h-[96px] transition-all ${
      done
        ? 'bg-accent/15 border-accent/40'
        : onClick
          ? 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10 cursor-pointer'
          : 'bg-white/5 border-white/10 hover:border-white/20'
    }`}
  >
    <div
      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
        done
          ? 'bg-accent text-white shadow-[0_3px_10px_-3px_rgba(241,110,0,0.6)]'
          : 'bg-white/10 text-white/55 border border-white/10'
      }`}
    >
      {done ? <Check size={14} strokeWidth={3} /> : <span className="text-[11px] font-bold">{index}</span>}
    </div>
    <p
      className={`text-[11.5px] leading-tight font-semibold tracking-[-0.005em] ${
        done ? 'text-white/65' : 'text-white'
      }`}
    >
      {label}
    </p>
  </div>
);
